import { Injectable } from '@angular/core';

import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, iif, of } from 'rxjs';
import { catchError, delayWhen, map, switchMap, take, tap, filter, timeout } from 'rxjs/operators';
import { MarketplacePartnerServicePermission } from '../modules/membership/modules/account/models/marketplace-partner-service-permission';
import { Person } from '../modules/membership/modules/account/models/person.model';
import { MarketplacePartnerAffiliationType } from '../modules/shared/enums/marketplace-partner-affiliation-types';
import { CompanyService, PartnerService, ProfileService } from '../modules/shared/services';
import { IframeAuthService } from '../modules/core/services/iframe-auth.service';
import { AuthService } from '../modules/core/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ProfilesPreloadGuard {

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private profileService: ProfileService,
    private companyService: CompanyService,
    private partnerService: PartnerService,
    private iframeAuthService: IframeAuthService,
    private authService: AuthService,
  ) { }

  canActivate(): Observable<boolean> {
    // Check if running in iframe mode
    if (this.authService.isIframeMode) {
      return this.checkIframeProfileLoad();
    }

    // Standalone mode - use OIDC
    return this.oidcSecurityService.checkAuth().pipe(
      take(1),
      map((response: LoginResponse) => response.isAuthenticated),
      delayWhen(isAuthenticated => iif(() => isAuthenticated, this.getPersonInfo(), of(false))),
    );
  }

  private checkIframeProfileLoad(): Observable<boolean> {
    const isAuthenticated = this.iframeAuthService.isAuthenticated;

    if (isAuthenticated) {
      // Already authenticated, load profile immediately
      console.log('[ProfilesPreloadGuard] Authenticated, loading profile...');
      return this.getPersonInfo();
    }

    // Wait for token from parent window
    console.log('[ProfilesPreloadGuard] Waiting for iframe authentication token...');
    return this.iframeAuthService.tokenReceived$
      .pipe(
        filter(received => received === true),  // Skip initial false value
        take(1),
        timeout(5000),  // Fail after 5 seconds
        switchMap(() => {
          if (this.iframeAuthService.isAuthenticated) {
            console.log('[ProfilesPreloadGuard] Token received, loading profile...');
            return this.getPersonInfo();
          }
          console.warn('[ProfilesPreloadGuard] Token received but authentication failed');
          return of(false);
        }),
        catchError((err) => {
          console.error('[ProfilesPreloadGuard] Timeout waiting for iframe authentication token', err);
          return of(false);
        })
      );
  }

  getPersonInfo(): Observable<boolean> {
    return this.profileService.getProfileInfo().pipe(
      take(1),
      tap(person => {
        const agsPermission = this.getAGSPartner(person);
        if (!!agsPermission) {
          this.validateAGSMembership();
        }
      }),
      switchMap(person => iif(() => !!person.companyId, this.getCompanyInfo(), of(true))),
      catchError(() => of(true))
    );
  }

  getCompanyInfo(): Observable<boolean> {
    return this.companyService.getCompanyProfile().pipe(
      take(1),
      map(() => true),
      catchError(() => of(true))
    );
  }

  private getAGSPartner(person: Person): MarketplacePartnerServicePermission {
    return this.partnerService.getPartnerPermission(
      MarketplacePartnerAffiliationType.AGS,
      person,
      true);
  }

  private validateAGSMembership() {
    this.partnerService.validatePartner(MarketplacePartnerAffiliationType[MarketplacePartnerAffiliationType.AGS])
      .pipe(take(1))
      .subscribe();
  }
}
